/* eslint-disable react/no-unstable-nested-components */

import { useQueryClient } from '@tanstack/react-query';
import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import { DataTableColumnHeader } from '@/components/DataTable/DataTableColumnHeader';
import { DataTablePagination } from '@/components/DataTable/DataTablePagination';
import TableFilters from '@/components/TableFilters/TableFilters';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { useDeptChairs } from '@/hooks/queries/useDeptChairs';
import { useGetUsers } from '@/hooks/queries/useUsers';
import { EditIcon, TrashCanIcon } from '@/icons/index';
import { supabase } from '@/lib/supabase';
import type { User, UserWithCourses } from '@/lib/types';

import CreateUserModal from './CreateUserModal/CreateUserModal';
import UserModal from './UserModal/UsersModal';

const Users = () => {
	const [modalActive, setModalActive] = useState(false);
	const [createUserModalActive, setCreateUserModalActive] = useState(false);
	const [isConfirming, setIsConfirming] = useState(false);
	const { data: userData, isFetching, isLoading } = useGetUsers();
	const [userList, setUserList] = useState<UserWithCourses[]>([]);
	const { data: deptChairData } = useDeptChairs();
	const [deptChairList, setDeptChairList] = useState<User[]>([]);
	const queryClient = useQueryClient();
	const [currentUser, setCurrentUser] = useState<UserWithCourses>();

	useEffect(() => {
		const abortController = new AbortController();
		if (userData) {
			setUserList(userData);
		}
		if (deptChairData) {
			setDeptChairList(deptChairData);
		}

		return () => {
			abortController.abort();
		};
	}, [userData, deptChairData]);

	const queryUserData = useMemo(
		() =>
			userList?.map((user) => ({
				nameColumn: `${user.user_first ?? ''} ${user.user_last ?? ''}`,
				courseColumn: user.courses_users,
				emailColumn: user.user_email,
				roleColumn: user.user_role,
				deptChairColumn: user.dept_chair,
				actionsColumn: user,
				idColumn: user.user_id,
			})),
		[userList]
	);

	const triggerModal = useCallback(() => {
		setModalActive(!modalActive);
	}, [modalActive]);

	const triggerCreateModal = useCallback(() => {
		setCreateUserModalActive(!createUserModalActive);
	}, [createUserModalActive]);

	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [filteredData, setFilteredData] = useState<UserTableData[]>([]);

	useEffect(() => {
		if (queryUserData) {
			setFilteredData(queryUserData as UserTableData[]);
		}
	}, [queryUserData]);

	const handleSetData = useCallback((data: any) => {
		setFilteredData(data as UserTableData[]);
	}, []);

	const confirmDelete = useCallback((user: any) => {
		setCurrentUser(user);
		setIsConfirming(true);
	}, []);

	const deleteUser = async () => {
		if (!currentUser) return;
		const { user_firebase_id: firebaseId } = currentUser;

		try {
			// Get the current Supabase session - user must be authenticated
			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (!session?.access_token) {
				throw new Error(
					'You must be signed into Supabase to delete users. Please sign out and sign in again.'
				);
			}

			const edgeFunctionResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-from-ui`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${import.meta.env.VITE_SERVICE_ROLE_KEY}`,
					},
					body: JSON.stringify({
						id: firebaseId,
					}),
				}
			);

			if (!edgeFunctionResponse.ok) {
				const errorData = await edgeFunctionResponse.json().catch(() => ({}));
				throw new Error(errorData.error || 'Failed to delete user');
			}

			showSuccessToast('User Deleted');
			queryClient.invalidateQueries({
				queryKey: ['get-users'],
			});
		} catch (error) {
			showErrorToast(error instanceof Error ? error.message : 'Failed to delete user');
		}
	};

	type UserTableData = {
		nameColumn: string;
		courseColumn: NonNullable<UserWithCourses['courses_users']> | undefined;
		emailColumn: string | null;
		roleColumn: string | null;
		deptChairColumn: User | null | undefined;
		actionsColumn: UserWithCourses;
		idColumn: string;
	};

	const columns = useMemo<ColumnDef<UserTableData>[]>(
		() => [
			{
				accessorKey: 'nameColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
				cell: ({ row }) => <div>{row.getValue('nameColumn')}</div>,
			},
			{
				accessorKey: 'roleColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Role" />,
				cell: ({ row }) => <div>{row.getValue('roleColumn')}</div>,
			},
			{
				accessorKey: 'courseColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Courses" />,
				cell: ({ row }) => {
					const courseArray = row.original.courseColumn;
					const courses = courseArray?.map((c) => c.course?.course_name).filter(Boolean) || [];
					const maxVisible = 2;
					const visibleCourses = courses.slice(0, maxVisible);
					const remainingCount = courses.length - maxVisible;

					if (!courses.length) {
						return <div className="text-muted-foreground">No courses</div>;
					}

					return (
						<Popover>
							<PopoverTrigger asChild>
								<button
									type="button"
									className="text-left hover:underline focus:outline-none focus:underline"
								>
									<div className="flex flex-col gap-1">
										{visibleCourses.map((courseName) => (
											<div key={courseName}>{courseName}</div>
										))}
										{remainingCount > 0 && (
											<div className="text-muted-foreground font-medium">
												+{remainingCount} more
											</div>
										)}
									</div>
								</button>
							</PopoverTrigger>
							<PopoverContent className="w-80" align="start">
								<div className="space-y-2">
									<h4 className="font-medium leading-none">All Courses</h4>
									<div className="text-[12px] text-muted-foreground">
										{courses.length} course{courses.length !== 1 ? 's' : ''} assigned
									</div>
									<div className="mt-4 max-h-[300px] overflow-y-auto">
										<div className="flex flex-col gap-2 typo-body-2">
											{courses.map((courseName) => (
												<div key={courseName} className="py-1 border-b last:border-b-0">
													{courseName}
												</div>
											))}
										</div>
									</div>
								</div>
							</PopoverContent>
						</Popover>
					);
				},
				enableSorting: false,
			},
			{
				accessorKey: 'deptChairColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Dept. Chair" />,
				cell: ({ row }) => {
					const deptChair = row.original.deptChairColumn;
					if (deptChair) {
						const firstName = deptChair.user_first ?? '';
						const lastName = deptChair.user_last ?? '';
						return (
							<div>
								{firstName} {lastName}
							</div>
						);
					}
					return <div />;
				},
				enableSorting: false,
			},
			{
				accessorKey: 'emailColumn',
				header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
				cell: ({ row }) => <div className="lowercase">{row.getValue('emailColumn')}</div>,
			},
			{
				id: 'actions',
				enableHiding: false,
				cell: ({ row }) => {
					const updateUserModal = () => {
						triggerModal();
						setCurrentUser(row.original.actionsColumn);
					};

					return (
						<div className="flex items-center justify-center [&>button:not(:last-child)]:mr-4">
							<EditIcon passedEvent={updateUserModal} />
							<TrashCanIcon passedEvent={() => confirmDelete(row.original.actionsColumn)} />
						</div>
					);
				},
			},
		],
		[triggerModal, confirmDelete]
	);

	const table = useReactTable({
		data: filteredData,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
		},
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	return (
		<>
			{modalActive && currentUser && (
				<UserModal
					modalData={currentUser as any}
					modalActive={modalActive}
					triggerModal={triggerModal}
					deptChairList={deptChairList}
				/>
			)}
			{createUserModalActive && (
				<CreateUserModal
					modalActive={createUserModalActive}
					triggerCreateModal={triggerCreateModal}
					deptChairList={deptChairList}
				/>
			)}
			<Button
				size="small"
				className="absolute z-10"
				onClick={() => setCreateUserModalActive(!createUserModalActive)}
			>
				Add New User
			</Button>
			{isConfirming && (
				<ConfirmModal
					modalActive={isConfirming}
					onConfirm={deleteUser}
					onCancel={() => setIsConfirming(false)}
					triggerModal={() => setIsConfirming(false)}
					message="Are you sure you want to delete this user?"
					selectedData={`${currentUser?.user_first} ${currentUser?.user_last}`}
				/>
			)}
			{queryUserData && (
				<TableFilters
					data={queryUserData}
					handleSetData={handleSetData}
					searchColumn={['nameColumn', 'emailColumn']}
					filteredColumn="roleColumn"
					resultQty={filteredData.length}
				/>
			)}
			{isLoading ||
				(isFetching && (
					<div className="flex flex-col pb-5 h-full">
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
						<Skeleton className="w-full mb-3 h-24" />
					</div>
				))}
			{!isFetching && (
				<div className="flex-1 flex flex-col min-h-0">
					<div
						className="flex-1 overflow-auto rounded-md"
						style={{ maxHeight: 'calc(100vh - 380px)' }}
					>
						<Table className="text-md typo-body-2">
							<TableHeader className="sticky top-0 bg-background z-10">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => {
											return (
												<TableHead key={header.id}>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</TableHead>
											);
										})}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows?.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={columns.length} className="h-24 text-center">
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
					<div className="mt-4 shrink-0 pb-4 border-t border-gray-200 pt-4">
						<DataTablePagination table={table} />
					</div>
				</div>
			)}
		</>
	);
};

export default Users;
